import React, { useEffect, useState } from "react";
import { Alert, Box, Button, Checkbox, Drawer, FormControlLabel, MenuItem, TextField, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import {
  useCreateSyncScheduleMutation,
  useGetAvailableSyncTasksQuery,
  useGetSyncScheduleQuery,
  useUpdateSyncScheduleMutation,
} from "state/rtkQuery/sync/syncQueries";

import { formatDateTime, INTERVAL_PERIODS, normalizeApiError } from "./syncFormat";
import { JsonBlock } from "./SyncParts";
import { DEDUP_PERIODS, emptyScheduleForm, formToBody, scheduleToForm, validateScheduleForm } from "./syncSchedules";

// name === null  -> create.  name === "x" -> edit the schedule called x (loaded fresh, any state, then saved in full).
const ScheduleDrawer = ({ name, onClose, onSaved }) => {
  const isCreate = name === null;
  const { data: available } = useGetAvailableSyncTasksQuery();
  const availableTasks = available?.available_tasks || [];
  // The catalogue says what each task is for and which arguments it takes (Celery arguments, not HTTP parameters).
  const catalogue = Object.fromEntries((available?.task_catalogue || []).map((entry) => [entry.task, entry]));
  const { data: stored, isFetching: loadingStored, isError: loadError, error: loadErr } = useGetSyncScheduleQuery({ name, includeLastTaskRun: true }, { skip: isCreate });
  const [createSchedule, { isLoading: creating }] = useCreateSyncScheduleMutation();
  const [updateSchedule, { isLoading: updating }] = useUpdateSyncScheduleMutation();

  const [form, setForm] = useState(emptyScheduleForm);
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const saving = creating || updating;

  useEffect(() => {
    if (isCreate) setForm(emptyScheduleForm());
    else if (stored && stored.name === name) setForm(scheduleToForm(stored));
  }, [isCreate, stored, name]);

  const set = (key, value) => {
    setForm((current) => ({ ...current, [key]: value }));
    if (errors[key]) setErrors((current) => ({ ...current, [key]: undefined }));
  };
  const input = (key, extra = {}) => ({
    value: form[key] ?? "",
    onChange: (event) => set(key, event.target.value),
    error: Boolean(errors[key]),
    helperText: errors[key] || extra.help,
    size: "small",
    fullWidth: true,
    sx: { mb: 2 },
  });

  const save = async () => {
    const found = validateScheduleForm(form, { isCreate, availableTasks });
    setErrors(found);
    setSubmitError(null);
    if (Object.keys(found).length) return;
    try {
      if (isCreate) {
        const body = formToBody(form);
        const response = await createSchedule(body).unwrap();
        onSaved(response?.message || `Schedule '${body.name}' created`);
      } else {
        const response = await updateSchedule({ name, body: formToBody(form, stored) }).unwrap();
        onSaved(response?.message || `Schedule '${name}' updated`);
      }
    } catch (error) {
      const normalized = normalizeApiError(error);
      setSubmitError(normalized);
      if (normalized.status === 409) setErrors({ name: normalized.message });
    }
  };

  const typeIsCron = form.schedule_type === "crontab";

  return (
    <Drawer anchor="right" open onClose={onClose} PaperProps={{ sx: { width: { xs: "100%", sm: 500 } } }}>
      <Box sx={{ p: "18px 22px", borderBottom: "1px solid #e2e8f0" }}>
        <Typography sx={{ color: "#6a9a1e", fontSize: 11.5, fontWeight: 700, letterSpacing: 1.2, textTransform: "uppercase" }}>{isCreate ? "New schedule" : "Edit schedule"}</Typography>
        <Typography sx={{ fontSize: 18, fontWeight: 700, mt: 0.25 }}>{isCreate ? "Create a schedule" : name}</Typography>
      </Box>

      <Box sx={{ p: "20px 22px", overflow: "auto", flex: 1 }}>
        {!isCreate && loadError && <Alert severity="error" sx={{ mb: 2 }}>{normalizeApiError(loadErr).message}</Alert>}
        {!isCreate && loadingStored && <Typography sx={{ color: "#64748b", mb: 2 }}>Loading the schedule…</Typography>}
        {submitError && <Alert severity={submitError.uncertain ? "warning" : "error"} sx={{ mb: 2 }} data-testid="schedule-error">{submitError.message}</Alert>}

        {!isCreate && stored && stored.name === name && (
          <Box sx={{ background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: "10px", p: "8px 14px", mb: 2, fontSize: 13 }} data-testid="schedule-meta">
            {[
              ["Created", [stored.created_by, stored.created_at && formatDateTime(stored.created_at)].filter(Boolean).join(" · ")],
              ["Last changed", [stored.updated_by, stored.updated_at && formatDateTime(stored.updated_at)].filter(Boolean).join(" · ")],
              ["Last run", stored.last_run_at ? formatDateTime(stored.last_run_at) : "Never"],
              ["Runs", Number(stored.total_run_count ?? 0).toLocaleString("en-US")],
              ["Estimated next run", stored.enabled === false ? "Paused" : stored.next_run_at ? formatDateTime(stored.next_run_at) : stored.next_run_unavailable_reason || "Not available"],
            ].filter(([, value]) => value).map(([label, value]) => (
              <Box key={label} sx={{ display: "flex", justifyContent: "space-between", py: 0.4, gap: 2 }}>
                <span style={{ color: "#64748b" }}>{label}</span>
                <b style={{ fontWeight: 600, textAlign: "right" }}>{value}</b>
              </Box>
            ))}
            <Typography sx={{ color: "#94a3b8", fontSize: 11.5, mt: 0.5 }}>These are set by the service and can't be edited. The next run is an estimate.</Typography>
            {stored.last_task_run && (
              <Box sx={{ borderTop: "1px solid #e2e8f0", mt: 1, pt: 1 }} data-testid="last-task-run">
                <Typography sx={{ fontWeight: 700, fontSize: 12.5 }}>Latest execution of this task</Typography>
                <Typography sx={{ color: "#64748b", fontSize: 12 }} data-testid="last-task-run-text">
                  {[
                    stored.last_task_run.status && `Status: ${stored.last_task_run.status}`,
                    stored.last_task_run.completed_at ? `completed ${formatDateTime(stored.last_task_run.completed_at)}` : "not completed yet",
                  ].filter(Boolean).join(" · ")}
                  . It may have been started by hand or by another schedule, and may still be running.
                </Typography>
                {stored.last_task_run.error && <Typography sx={{ color: "#b91c1c", fontSize: 12 }}>{typeof stored.last_task_run.error === "string" ? stored.last_task_run.error : JSON.stringify(stored.last_task_run.error)}</Typography>}
              </Box>
            )}
          </Box>
        )}
        <TextField label="Name *" disabled={!isCreate} {...input("name", { help: isCreate ? "Used as the identity in URLs. Must be unique. It can't be changed later." : "The name can't be changed." })} />
        <TextField select label="Task *" {...input("task", { help: catalogue[form.task]?.description || "Only registered sync tasks are offered." })}>
          {[...new Set([form.task, ...availableTasks].filter(Boolean))].map((task) => (
            <MenuItem key={task} value={task}>
              {task}
              {catalogue[task]?.scheduling_recommended === false ? " (not usually scheduled)" : ""}
            </MenuItem>
          ))}
        </TextField>
        {catalogue[form.task]?.scheduling_recommended === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>This task isn't normally run on a schedule.</Alert>
        )}
        <TextField label="Description" {...input("description")} />

        <Typography sx={{ fontSize: 12.5, fontWeight: 600, mb: 0.75 }}>Repeat</Typography>
        <ToggleButtonGroup exclusive size="small" value={form.schedule_type} onChange={(_, value) => value && set("schedule_type", value)} sx={{ mb: 2 }}>
          <ToggleButton value="interval" sx={{ textTransform: "none" }}>Every N …</ToggleButton>
          <ToggleButton value="crontab" sx={{ textTransform: "none" }}>Cron</ToggleButton>
        </ToggleButtonGroup>

        {!typeIsCron ? (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField label="Every" type="number" inputProps={{ min: 1 }} {...input("every")} />
            <TextField select label="Period" {...input("period")}>
              {INTERVAL_PERIODS.map((period) => <MenuItem key={period} value={period}>{period}</MenuItem>)}
            </TextField>
          </Box>
        ) : (
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
            <TextField label="Minute" {...input("minute")} />
            <TextField label="Hour" {...input("hour")} />
            <TextField label="Day of month" {...input("day_of_month")} />
            <TextField label="Month" {...input("month_of_year")} />
            <Box sx={{ gridColumn: "1 / -1" }}>
              <TextField label="Day of week" {...input("day_of_week", { help: "Times are UTC. Example: 0 3 * * * runs every day at 03:00 UTC." })} />
            </Box>
          </Box>
        )}

        <TextField label="Time zone" value="UTC" disabled size="small" fullWidth helperText="Schedules run in UTC." sx={{ mb: 2 }} />

        <Box sx={{ border: "1px dashed #cbd5e1", borderRadius: "10px", p: "4px 14px 14px", background: "#fafbfc", mb: 2 }}>
          <Typography sx={{ fontWeight: 600, fontSize: 12.5, color: "#64748b", pt: 1.25, pb: 1 }}>Advanced</Typography>
          <TextField label="Queue" placeholder="default for the task" {...input("queue")} />
          <TextField label="Arguments (JSON array)" multiline minRows={2} {...input("argsText")} />
          <TextField label="Keyword arguments (JSON object)" multiline minRows={3} {...input("kwargsText", { help: "These are task arguments, not the HTTP query parameters. Don't put queue here." })} />
          {catalogue[form.task]?.arguments_schema && (
            <details style={{ marginBottom: 12 }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, color: "#64748b" }}>Arguments this task accepts</summary>
              <Box data-testid="arguments-schema"><JsonBlock value={catalogue[form.task].arguments_schema} /></Box>
            </details>
          )}
          <FormControlLabel
            control={<Checkbox size="small" checked={form.dedupEnabled} onChange={(event) => set("dedupEnabled", event.target.checked)} />}
            label={<span><b>Skip if it ran recently</b><br /><span style={{ color: "#64748b", fontSize: 12 }}>The task's own duplicate guard.</span></span>}
            sx={{ alignItems: "flex-start" }}
          />
          {form.dedupEnabled && (
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5, mt: 1 }}>
              <TextField label="Window" type="number" inputProps={{ min: 1 }} {...input("dedupWindow")} />
              <TextField select label="Period" {...input("dedupPeriod")}>
                {DEDUP_PERIODS.map((period) => <MenuItem key={period} value={period}>{period}</MenuItem>)}
              </TextField>
            </Box>
          )}
        </Box>

        <FormControlLabel control={<Checkbox size="small" checked={form.enabled} onChange={(event) => set("enabled", event.target.checked)} />} label={<b>Enabled</b>} />
        <Alert severity="info" sx={{ mt: 2 }}>
          Changes are saved straight away; the scheduler picks them up within about 5 minutes. Saving never runs the task.
        </Alert>
      </Box>

      <Box sx={{ p: "14px 22px", borderTop: "1px solid #e2e8f0", display: "flex", justifyContent: "flex-end", gap: 1.5, background: "#fafbfc" }}>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || (!isCreate && (loadingStored || loadError))} sx={{ background: "#86BC25", textTransform: "none", fontWeight: 700, boxShadow: "none", "&:hover": { background: "#6a9a1e", boxShadow: "none" } }}>
          {saving ? "Saving…" : "Save schedule"}
        </Button>
      </Box>
    </Drawer>
  );
};

export default ScheduleDrawer;
